import React, { useEffect, useState, useContext, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";
import AppContext from "../../../utilities/contexts/appContext";
import SdkContext from "../../../utilities/contexts/sdk/sdkContext";
import SizeGuideResult from "./SizeGuideResult";
import { backUrl } from "../../../utilities/Api/Urls";

export interface QuestionnaireStep {
  brand: string;
  gender: string;
  category: string;
  type: string;
  sizeType: string;
}

export interface SizeGuideRequest {
  KeyList: string[];
  Size: Record<string, any>;
}

const SizeGuideQuestionnaire: React.FC = () => {
  const location = useLocation();
  const { bodyMeasurement } = useUnifiedService();
  const { measurementData, setIsClosestMatch } = bodyMeasurement;
  const appContext = useContext(AppContext);
  const sdkContext = useContext(SdkContext);

  const isSDK = location.pathname.includes("/sdk/");
  // If requestId exists, always use SDK Token (client-specific request)
  // Otherwise: Main Token (if logged in) > SDK Token (fallback)
  const authToken = isSDK ? (sdkContext.requestId ? sdkContext.token : appContext.token || sdkContext.token) : appContext.token;

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [selections, setSelections] = useState<QuestionnaireStep>({
    brand: "",
    gender: "",
    category: "",
    type: "",
    sizeType: "",
  });

  const [hasSizeTypes, setHasSizeTypes] = useState(false);
  const [types, setTypes] = useState<string[]>([]);
  const [sizeTypes, setSizeTypes] = useState<string[]>([]);

  const [availableCharts, setAvailableCharts] = useState<any>(null);
  const [sizeGuideResult, setSizeGuideResult] = useState<any>(null);
  const [loadingSizeGuide, setLoadingSizeGuide] = useState(false);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isBrandAutoFilled, setIsBrandAutoFilled] = useState(false);

  const initializeCharts = useCallback(async () => {
    if (!authToken) {
      setError("Authentication required");
      return;
    }

    try {
      setLoadingCharts(true);
      setError(null);

      let profile = null;
      try {
        const profileResponse = await fetch(`${backUrl}/api/app/v1/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          if (profileData.ok && profileData.status_code === 200) {
            profile = profileData.data;
          }
        }
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      }

      const chartsResponse = await fetch(`${backUrl}/api/v1/services/available_charts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!chartsResponse.ok) {
        throw new Error(`Failed to fetch charts: ${chartsResponse.status}`);
      }

      const chartsData = await chartsResponse.json();
      if (!chartsData.ok || chartsData.status_code !== 200) {
        throw new Error(chartsData.message || "Failed to load size charts");
      }

      let availableChartsData = chartsData.data;

      let userBrandSelected = false;
      if (profile && profile.id) {
        const userId = profile.id.toString();
        if (availableChartsData[userId]) {
          setSelections((prev) => ({ ...prev, brand: userId }));
          setCurrentStep(0);
          setIsBrandAutoFilled(true);
          userBrandSelected = true;
        }
      }

      if (!userBrandSelected) {
        const allowedBrands = ["Canadagoose", "KitAndAce", "Knix", "Lululemon", "OakAndFort", "Patagonia", "Roots", "Vessi"];
        const filteredData: any = {};

        allowedBrands.forEach((brand) => {
          if (availableChartsData[brand]) {
            filteredData[brand] = availableChartsData[brand];
          }
        });

        availableChartsData = filteredData;
      }

      setAvailableCharts(availableChartsData);
    } catch (error: any) {
      console.error("Error initializing charts:", error);
      setError("Failed to load size charts");
    } finally {
      setLoadingCharts(false);
    }
  }, [authToken]);

  useEffect(() => {
    initializeCharts();
  }, []);

  const submitSizeGuide = async (sizeGuideData: SizeGuideRequest) => {
    if (!authToken) {
      setError("Authentication required");
      return;
    }

    try {
      setLoadingSizeGuide(true);
      setError(null);

      const response = await fetch(`${backUrl}/api/v1/services/size_guide`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(sizeGuideData),
      });

      if (!response.ok) {
        throw new Error(`Size guide request failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.ok && data.status_code === 200) {
        if (data.status_code === 206) {
          setIsClosestMatch(data.message);
        } else {
          setIsClosestMatch(null);
        }
        setSizeGuideResult(data.data);
      } else if (data.status_code === 206) {
        setIsClosestMatch(data.message);
        setSizeGuideResult(data.data);
      } else {
        throw new Error(data.message || "Size guide is not available");
      }
    } catch (error: any) {
      setError("Failed to get size guide");
      setSizeGuideResult(null);
    } finally {
      setLoadingSizeGuide(false);
    }
  };

  useEffect(() => {
    if (sizeGuideResult === null) {
      setCurrentStep(0);
      setSelectedItem("");
      setHasSizeTypes(false);
      setTypes([]);
      setSizeTypes([]);
      setSelections({
        brand: "",
        gender: "",
        category: "",
        type: "",
        sizeType: "",
      });
    }
  }, [sizeGuideResult]);

  useEffect(() => {
    if (!availableCharts) return;

    let newTypes: string[] = [];
    let hasTypes = false;

    if (selections.brand && selections.gender && selections.category) {
      const categoryData = availableCharts[selections.brand]?.[selections.gender]?.[selections.category];
      if (categoryData) {
        newTypes = Object.keys(categoryData);

        const firstType = newTypes[0];
        if (firstType && categoryData[firstType] && typeof categoryData[firstType] === "object") {
          const sizeTypeKeys = Object.keys(categoryData[firstType]);
          hasTypes = sizeTypeKeys.some((key) => key === "Regular" || key === "PlusSize" || key === "PLusSize");
        }
      }
    }

    setTypes(newTypes);
    setHasSizeTypes(hasTypes);
  }, [availableCharts, selections.brand, selections.gender, selections.category]);

  useEffect(() => {
    if (!availableCharts || !selections.type) {
      setSizeTypes([]);
      return;
    }

    let newSizeTypes: string[] = [];

    if (selections.brand && selections.gender && selections.category && selections.type) {
      const typeData = availableCharts[selections.brand]?.[selections.gender]?.[selections.category]?.[selections.type];

      if (typeData && typeof typeData === "object") {
        newSizeTypes = Object.keys(typeData);
        setHasSizeTypes(newSizeTypes.length > 0);
      }
    }

    setSizeTypes(newSizeTypes);
  }, [availableCharts, selections.type, selections.brand, selections.gender, selections.category]);

  const handleFullReset = () => {
    setSizeGuideResult(null);
    setIsClosestMatch(null);
    setError(null);
    setCurrentStep(0);
    setHasSizeTypes(false);
    setTypes([]);
    setSizeTypes([]);
    setSelectedItem("");
    setIsBrandAutoFilled(false);
    setSelections({
      brand: "",
      gender: "",
      category: "",
      type: "",
      sizeType: "",
    });

    initializeCharts();
  };

  if (!availableCharts || loadingCharts) {
    return (
      <div className="flex flex-col items-center gap-4 p-8">
        <div className="flex items-center justify-center w-8 h-8 border-4 border-gray-300 rounded-full border-t-primary animate-spin"></div>
        <p className="text-gray-600">Loading size charts...</p>
      </div>
    );
  }

  const brands = Object.keys(availableCharts);
  const genders = selections.brand ? Object.keys(availableCharts[selections.brand]) : [];
  const categories = selections.gender ? Object.keys(availableCharts[selections.brand][selections.gender]) : [];

  const allSteps = [
    {
      key: "brand",
      title: "Brand",
      description: "Choose your preferred brand",
      options: brands,
    },
    {
      key: "gender",
      title: "Gender",
      description: "Select the gender category",
      options: genders,
    },
    {
      key: "category",
      title: "Category",
      description: "Choose product category",
      options: categories,
    },
    {
      key: "type",
      title: "Size",
      description: "Select your preferred size guide by country",
      options: types,
    },
    {
      key: "sizeType",
      title: "Fit",
      description: "Select your preferred fit",
      options: sizeTypes,
    },
  ];

  const steps = (() => {
    let filteredSteps = [...allSteps];

    if (isBrandAutoFilled) {
      filteredSteps = filteredSteps.filter((step) => step.key !== "brand");
    }

    if (!hasSizeTypes) {
      filteredSteps = filteredSteps.filter((step) => step.key !== "sizeType");
    }

    return filteredSteps;
  })();

  const handleItemSelect = (value: string) => {
    setSelectedItem(value);
  };

  const handleConfirmSelection = async () => {
    if (!selectedItem) return;

    const currentVisualStep = steps[currentStep];
    const key = currentVisualStep.key as keyof QuestionnaireStep;

    const newSelections = { ...selections, [key]: selectedItem };

    if (key === "brand") {
      newSelections.gender = "";
      newSelections.category = "";
      newSelections.type = "";
      newSelections.sizeType = "";
      setSelections(newSelections);
      setCurrentStep(currentStep + 1);
    } else if (key === "gender") {
      newSelections.category = "";
      newSelections.type = "";
      newSelections.sizeType = "";
      setSelections(newSelections);
      setCurrentStep(currentStep + 1);
    } else if (key === "category") {
      newSelections.type = "";
      newSelections.sizeType = "";
      setSelections(newSelections);
      setCurrentStep(currentStep + 1);
    } else if (key === "type") {
      newSelections.sizeType = "";
      setSelections(newSelections);

      if (hasSizeTypes) {
        const typeData = availableCharts[newSelections.brand]?.[newSelections.gender]?.[newSelections.category]?.[selectedItem];
        if (typeData && typeof typeData === "object" && Object.keys(typeData).length > 0) {
          setCurrentStep(currentStep + 1);
          return;
        }
      }
      submitSizeGuideData(newSelections);
    } else if (key === "sizeType") {
      setSelections(newSelections);
      submitSizeGuideData(newSelections);
    }

    setSelectedItem("");
  };

  const submitSizeGuideData = (selectionData: QuestionnaireStep) => {
    const sizeData = measurementData
      ? Object.entries(measurementData)
          .filter(([key]) => !["Details", "MetaData", "FullViewImage", "SideViewImage"].includes(key))
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      : {};

    const keyList = [];
    keyList.push(selectionData.brand);
    keyList.push(selectionData.gender);
    keyList.push(selectionData.category);
    keyList.push(selectionData.type);

    if (hasSizeTypes && selectionData.sizeType) {
      keyList.push(selectionData.sizeType);
    }

    const sizeGuideData: SizeGuideRequest = {
      KeyList: keyList,
      Size: sizeData,
    };

    setCurrentStep(steps.length);
    submitSizeGuide(sizeGuideData);
  };

  const handlePrevStep = () => {
    if (sizeGuideResult) {
      handleFullReset();
      return;
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setSelectedItem("");
    }
  };

  const renderStepContent = (step: (typeof steps)[0]) => (
    <div className="grid grid-cols-2 gap-4 overflow-hidden overflow-y-auto">
      {step.options.map((option: any) => (
        <button
          key={option}
          onClick={() => handleItemSelect(option)}
          className={`py-3 px-4 text-sm font-medium transition-all duration-200 border rounded-lg 
            ${selectedItem === option ? "border-primary bg-primary text-white" : "border-gray-200 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"}`}
        >
          {option}
        </button>
      ))}
    </div>
  );
  const displaySteps = [...steps, { title: "Results", key: "results" }];

  const getGridColsClass = (stepsCount: number) => {
    switch (stepsCount) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-2";
      case 3:
        return "grid-cols-3";
      case 4:
        return "grid-cols-4";
      case 5:
        return "grid-cols-5";
      case 6:
        return "grid-cols-6";
      default:
        return "grid-cols-5";
    }
  };

  const gridColsClass = getGridColsClass(displaySteps.length);

  return (
    <div className={`flex flex-col items-center w-fit gap-4 md:gap-6 md:p-6 ${!sizeGuideResult ? "max-w-[40rem]" : ""}`}>
      {!sizeGuideResult ? (
        <div className={`relative grid gap-2 ${gridColsClass} *:flex-grow`}>
          {displaySteps.map((step, index) => (
            <div key={index} className="relative flex flex-col items-center">
              {index < displaySteps.length - 1 && <div className={`block absolute top-5 left-full -translate-x-1/2 w-full h-[2px] ${index < currentStep ? "bg-primary" : "bg-gray-200"}`} />}

              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-600 bg-white z-20 ${
                  index <= currentStep ? "border-primary bg-primary/10" : "border-gray-200"
                }`}
              >
                {index + 1}
              </div>
              <span className={`hidden md:block mt-3.25 text-xs font-medium ${index <= currentStep ? "text-primary" : "text-gray-400"}`}>{step.title}</span>
            </div>
          ))}
        </div>
      ) : null}

      {currentStep < steps.length ? (
        <div className="">
          <h2 className="text-lg font-bold text-secondary">{steps[currentStep].title}</h2>
          <p className="text-base text-gray-600">{steps[currentStep].description}</p>
          {renderStepContent(steps[currentStep])}
        </div>
      ) : loadingSizeGuide ? (
        <div className="flex flex-col items-center gap-4 p-8">
          <div className="flex items-center justify-center w-8 h-8 border-4 border-gray-300 rounded-full border-t-primary animate-spin"></div>
          <p className="text-gray-600">Getting your size recommendations...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-4 p-4">
          <h2 className="text-center text-red-600">{error}</h2>
          <button onClick={handleFullReset} className="px-6 py-3 text-sm font-medium text-white transition-all duration-200 border rounded-lg border-primary bg-primary hover:bg-primary/90">
            Try Different Options
          </button>
        </div>
      ) : null}

      {sizeGuideResult && <SizeGuideResult results={sizeGuideResult} gender={(selections.gender?.toLowerCase() || "genderfree") as "men" | "women" | "genderfree"} />}
      {!error && !sizeGuideResult ? (
        <div className="flex justify-between w-full mt-4">
          <div className="flex items-center justify-between w-full gap-4">
            <button
              onClick={handlePrevStep}
              disabled={currentStep <= 0}
              className={` py-2 px-6 text-sm font-medium transition-all duration-200 border rounded-lg
                  ${currentStep <= 0 ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"}`}
            >
              Previous
            </button>
            {currentStep < steps.length && (
              <button
                onClick={handleConfirmSelection}
                disabled={!selectedItem}
                className={` py-2 px-6 text-sm font-medium transition-all duration-200 border rounded-lg
                    ${!selectedItem ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-primary bg-primary text-white hover:bg-primary/90"}`}
              >
                {currentStep === steps.length - 1 ? "Show Results" : "Next"}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SizeGuideQuestionnaire;
